import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from './ui/card';
import { useProject } from '@/components/ProjectSelector';
import { usePermissions } from '@/contexts/PermissionsContext';
import { useNavigation } from '@/contexts/NavigationContext';
import { filterNavigation, navigationItems } from './navigation-items';
import '@/design-system/typography.css';

/**
 * Widgets of the client Home, in order. EMPTY on purpose: the Home of a client shows no figure until each one is designed and its source is client-safe
 * (never a budget, a cost, a margin or a count of the company's internal work). A future KPI is one entry here, backed by an endpoint that returns client-safe data.
 */
export const CLIENT_DASHBOARD_WIDGETS: ReadonlyArray<{ id: string; render: () => React.ReactNode }> = Object.freeze([]);

/** The Home of a client account: where they are and the sections they can open. No query, no internal KPI. */
export function ClientDashboard() {
  const navigate = useNavigate();
  const { selectedProject, projects } = useProject();
  const { can } = usePermissions();
  const { isVisibleKey } = useNavigation();
  const sections = useMemo(
    () => filterNavigation(navigationItems, can, isVisibleKey)
      .flatMap((item) => (item.submenu ? item.submenu : [item]))
      .filter((item) => item.href && item.href !== '/' && item.href !== '/settings'),
    [can, isVisibleKey],
  );
  const projectName = selectedProject && selectedProject !== 'all' ? projects.find((p) => p.id === selectedProject)?.name : null;

  return (
    <div className="p-6 space-y-6 tracking-tight" data-testid="client-dashboard">
      <div>
        <h1 className="text-xl font-semibold">Welcome</h1>
        <p className="text-sm text-gray-600">{projectName ? `Your project: ${projectName}` : 'Choose your project at the top to see what is shared with you.'}</p>
      </div>
      {CLIENT_DASHBOARD_WIDGETS.map((widget) => <div key={widget.id}>{widget.render()}</div>)}
      {sections.length > 0 && (
        <Card className="p-6">
          <h2 className="mb-3 text-sm font-medium text-gray-700">Shared with you</h2>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {sections.map((section) => (
              <button
                key={section.href}
                type="button"
                onClick={() => navigate(section.href as string)}
                className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-left text-sm hover:bg-gray-50"
              >
                {section.icon}
                <span>{section.title}</span>
              </button>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
